import { useState } from 'react';
import { Link as LinkIcon, Loader2, Check, X, AlertTriangle } from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { financeApi } from '../lib/api';
import { toast } from 'sonner';

/**
 * LinkBookedInvoiceDialog
 *
 * Manual recovery path when auto-sync can't resolve a draft → booked
 * transition (e.g. draft was deleted manually in e-conomic and a new
 * invoice was created from scratch, or customer/amount diverged and
 * the heuristic search couldn't connect the dots).
 *
 * Flow:
 *   1. Admin enters the booked invoice number from e-conomic.
 *   2. Backend fetches the invoice + returns customer/amount comparison
 *      against what OwnTune expects. We render those as green/red badges.
 *   3. Admin confirms — backend persists the link AND flips the order to
 *      Completed (same side-effects as auto-sync). Old draft number is
 *      kept in `economic_draft_invoice_number_history` for audit.
 *   4. If the invoice ALREADY has an economic link, the backend returns
 *      HTTP 409 with the existing link details — we render an inline
 *      confirmation block and let the admin resubmit with
 *      confirm_replace=true.
 */
export const LinkBookedInvoiceDialog = ({ open, onOpenChange, orderId, onLinked }) => {
    const [bookedNumber, setBookedNumber] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    // ``previewData`` = read-only response from the preview endpoint,
    // shown to the admin BEFORE anything is committed. Populated by
    // handleFetchPreview + cleared when the admin backs out.
    const [previewData, setPreviewData] = useState(null);
    // ``result`` = post-link response (only set after Confirm & link).
    const [result, setResult] = useState(null);
    // Populated when the backend returns 409 — surfaces the existing
    // link so the admin has full context before overwriting.
    const [replaceConflict, setReplaceConflict] = useState(null);

    const reset = () => {
        setBookedNumber('');
        setPreviewData(null);
        setResult(null);
        setReplaceConflict(null);
        setIsSubmitting(false);
    };

    const handleFetchPreview = async () => {
        const n = parseInt(bookedNumber, 10);
        if (!Number.isFinite(n) || n <= 0) {
            toast.error('Please enter a valid booked invoice number');
            return;
        }
        setIsSubmitting(true);
        try {
            const res = await financeApi.previewEconomicInvoice(orderId, n);
            setPreviewData(res.data);
        } catch (err) {
            const detail = err.response?.data?.detail || 'Failed to fetch invoice from e-conomic';
            toast.error(typeof detail === 'string' ? detail : 'Failed to fetch invoice');
        } finally {
            setIsSubmitting(false);
        }
    };

    const submitLink = async (confirmReplace) => {
        const n = parseInt(bookedNumber, 10);
        if (!Number.isFinite(n) || n <= 0) return;
        setIsSubmitting(true);
        try {
            const res = await financeApi.linkBookedInvoice(orderId, n, confirmReplace);
            setResult(res.data);
            setReplaceConflict(null);
            toast.success(`Linked booked invoice #${n}`);
            if (typeof onLinked === 'function') await onLinked();
        } catch (err) {
            const detail = err.response?.data?.detail;
            if (err.response?.status === 409 && detail && typeof detail === 'object' && detail.code === 'existing_link_conflict') {
                setReplaceConflict(detail);
            } else {
                const msg = typeof detail === 'string' ? detail : 'Failed to link booked invoice';
                toast.error(msg);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleConfirmLink = () => submitLink(false);
    const handleConfirmReplace = () => submitLink(true);

    const handleClose = (next) => {
        if (!next) reset();
        onOpenChange(next);
    };

    const fmtAmount = (n, ccy) => {
        if (n == null) return '—';
        try { return `${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${ccy || ''}`.trim(); }
        catch { return String(n); }
    };

    // Compact renderer for the preview comparison card
    const renderComparisonRow = (label, valueRight, ok, note) => (
        <div className="flex justify-between items-center gap-2">
            <span className="text-slate-500">{label}</span>
            <span className="font-mono text-slate-800">{valueRight ?? '—'}</span>
            {ok === true && <Check className="h-4 w-4 text-emerald-600 shrink-0" />}
            {ok === false && (
                <span className="flex items-center gap-1 text-amber-700 text-[11px]">
                    <X className="h-3.5 w-3.5" /> {note || 'differs'}
                </span>
            )}
        </div>
    );

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-md" data-testid="link-booked-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <LinkIcon className="h-4 w-4" />
                        Link existing e-conomic invoice
                    </DialogTitle>
                    <DialogDescription>
                        Attach an existing booked invoice from e-conomic to this
                        OwnTune record. Nothing in e-conomic will be created,
                        booked, sent, or modified — only the OwnTune-side pointer
                        is updated.
                    </DialogDescription>
                </DialogHeader>

                {/* STATE 1: Replace-existing conflict — asked by backend on 409 */}
                {replaceConflict ? (
                    <div className="space-y-2 py-2 text-sm" data-testid="link-replace-conflict">
                        <div className="flex gap-1.5 items-start text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-2">
                            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                            <div>
                                <div className="font-semibold">
                                    This invoice is already linked to e-conomic
                                </div>
                                <div className="mt-1">
                                    Replacing the link will NOT modify anything in
                                    e-conomic — only the OwnTune-side pointer will
                                    change. The previous identifiers are kept in
                                    the audit history.
                                </div>
                            </div>
                        </div>
                        <div className="rounded border bg-slate-50 px-3 py-2 space-y-1">
                            <div className="flex justify-between">
                                <span className="text-slate-500">Currently linked</span>
                                <span className="font-mono">
                                    {replaceConflict.existing?.economic_booked_invoice_number
                                        ? `#${replaceConflict.existing.economic_booked_invoice_number} (booked)`
                                        : replaceConflict.existing?.economic_draft_number
                                            ? `#${replaceConflict.existing.economic_draft_number} (draft)`
                                            : '—'}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-slate-500">Will be replaced with</span>
                                <span className="font-mono">
                                    #{replaceConflict.incoming_booked_invoice_number} (booked)
                                </span>
                            </div>
                        </div>
                    </div>
                ) : result ? (
                    /* STATE 4: Success confirmation */
                    <div className="space-y-2 py-2 text-sm">
                        <p className="font-semibold text-emerald-700 flex items-center gap-1.5">
                            <Check className="h-4 w-4" /> Linked successfully
                        </p>
                        <div className="rounded border bg-slate-50 px-3 py-2 space-y-1">
                            <div className="flex justify-between">
                                <span className="text-slate-500">Booked invoice</span>
                                <span className="font-mono">#{result.booked_invoice_number}</span>
                            </div>
                            {result.previous_draft_number ? (
                                <div className="flex justify-between">
                                    <span className="text-slate-500">Previous draft (kept as history)</span>
                                    <span className="font-mono text-slate-400 line-through">#{result.previous_draft_number}</span>
                                </div>
                            ) : null}
                        </div>
                    </div>
                ) : previewData ? (
                    /* STATE 3: Preview before committing */
                    <div className="space-y-3 py-2 text-sm" data-testid="link-preview">
                        <div className="rounded border bg-slate-50 px-3 py-2 space-y-1.5">
                            <div className="flex justify-between font-semibold text-slate-800">
                                <span>Invoice #{previewData.booked?.bookedInvoiceNumber}</span>
                                <span className="font-mono text-xs text-slate-500">
                                    {previewData.booked?.date || '—'}
                                </span>
                            </div>
                            {renderComparisonRow(
                                'Customer',
                                previewData.comparison?.booked_customer_number
                                    ? `#${previewData.comparison.booked_customer_number}`
                                    : '—',
                                previewData.comparison?.customer_match,
                                previewData.comparison?.expected_customer_number
                                    ? `expected #${previewData.comparison.expected_customer_number}`
                                    : 'no customer_number on org',
                            )}
                            {renderComparisonRow(
                                'Amount',
                                fmtAmount(previewData.booked?.grossAmount, previewData.booked?.currency),
                                previewData.comparison?.amount_match,
                                `expected ${fmtAmount(previewData.comparison?.expected_amount, previewData.comparison?.expected_currency)}`,
                            )}
                            {renderComparisonRow(
                                'Currency',
                                previewData.comparison?.booked_currency,
                                previewData.comparison?.currency_match,
                                `expected ${previewData.comparison?.expected_currency || '—'}`,
                            )}
                        </div>
                        {(previewData.comparison?.customer_match === false
                            || previewData.comparison?.amount_match === false
                            || previewData.comparison?.currency_match === false) ? (
                            <div className="flex gap-1.5 items-start text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                <span>
                                    Some fields don&apos;t match. Verify this really is the correct
                                    e-conomic invoice before you confirm the link.
                                </span>
                            </div>
                        ) : null}
                    </div>
                ) : (
                    /* STATE 2: Enter booked invoice number */
                    <div className="space-y-3 py-2">
                        <Label htmlFor="booked-number">Booked invoice number</Label>
                        <Input
                            id="booked-number"
                            value={bookedNumber}
                            onChange={(e) => setBookedNumber(e.target.value.replace(/\D/g, ''))}
                            inputMode="numeric"
                            placeholder="e.g. 22507"
                            disabled={isSubmitting}
                            data-testid="link-booked-input"
                        />
                        <p className="text-xs text-slate-500">
                            Find this number in e-conomic under <em>Invoices → Booked</em>.
                        </p>
                    </div>
                )}

                <DialogFooter>
                    {replaceConflict ? (
                        <>
                            <Button
                                variant="ghost"
                                onClick={() => setReplaceConflict(null)}
                                disabled={isSubmitting}
                                data-testid="link-replace-back"
                            >
                                Back
                            </Button>
                            <Button
                                onClick={handleConfirmReplace}
                                disabled={isSubmitting}
                                className="bg-amber-600 hover:bg-amber-700 text-white"
                                data-testid="link-replace-confirm"
                            >
                                {isSubmitting && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                                Replace existing link
                            </Button>
                        </>
                    ) : result ? (
                        <Button onClick={() => handleClose(false)} data-testid="link-booked-done">
                            Done
                        </Button>
                    ) : previewData ? (
                        <>
                            <Button
                                variant="ghost"
                                onClick={() => setPreviewData(null)}
                                disabled={isSubmitting}
                                data-testid="link-preview-back"
                            >
                                Back
                            </Button>
                            <Button
                                onClick={handleConfirmLink}
                                disabled={isSubmitting}
                                data-testid="link-preview-confirm"
                            >
                                {isSubmitting && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                                Confirm & link
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button variant="ghost" onClick={() => handleClose(false)} disabled={isSubmitting}>
                                Cancel
                            </Button>
                            <Button
                                onClick={handleFetchPreview}
                                disabled={isSubmitting || !bookedNumber}
                                data-testid="link-booked-fetch"
                            >
                                {isSubmitting && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                                Fetch preview
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default LinkBookedInvoiceDialog;
